import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Btn, Modal, Select, Spinner } from './UI';
import { api, getUser } from '../utils/api';
import UsageAlert from './UsageAlert';

// HB Sales Tool W6 — translation of HB content (Units, Expedition Logs).
// Approved language versions are for everyone; a draft is the author's until shared;
// the custodian of the family approves it for the shelf.

const STATUS = { draft: 'draft — only you see it', shared: 'shared — waiting for the custodian', approved: 'approved', declined: 'declined' };

export function TranslateButton({ type, entityKey, label = 'Translate', size = 'sm' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Btn size={size} variant="outline" onClick={() => setOpen(true)}>{label}</Btn>
      {open && <TranslateModal type={type} entityKey={entityKey} onClose={() => setOpen(false)} />}
    </>
  );
}

function TranslateModal({ type, entityKey, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('SR');
  const [busy, setBusy] = useState('');
  const [view, setView] = useState(null);      // the translation shown
  const [edit, setEdit] = useState(null);      // { title, body } while editing a draft

  const load = useCallback(() => api.translations.list(type, entityKey)
    .then(d => { setData(d); setView(v => (v ? d.translations.find(t => t.id === v.id) || null : d.translations.find(t => t.status === 'approved') || null)); })
    .catch(e => setError(e.message)), [type, entityKey]);
  useEffect(() => { load(); }, [load]);

  async function act(name, fn) {
    setBusy(name); setError(null);
    try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setBusy(''); }
  }
  const draft = () => act('draft', async () => { const r = await api.translations.create(type, entityKey, lang); setView({ id: r.id }); });
  const me = getUser()?.id;

  return (
    <Modal open onClose={onClose} width={820} title={`Translate — ${entityKey}`}>
      <UsageAlert />
      {error && <Alert type="error">{error}</Alert>}
      {!data ? <Spinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select value={lang} onChange={e => setLang(e.target.value)} style={{ width: 'auto' }}>
              {Object.entries(data.languages).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
            <Btn size="sm" loading={busy === 'draft'} onClick={draft}>✦ Draft a translation</Btn>
            <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Hansen Beck terms (Unit, Ice Pilot, Expedition Log, HB Compass …) stay as they are.</span>
          </div>

          {data.translations.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {data.translations.map(t => (
                <button key={t.id} onClick={() => { setView(t); setEdit(null); }}
                  style={{ padding: '5px 12px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                    border: '1px solid var(--line)', background: view?.id === t.id ? 'var(--ink)' : '#fff', color: view?.id === t.id ? '#fff' : 'var(--ink)' }}>
                  {t.lang} · {t.status}
                </button>
              ))}
            </div>
          )}

          {view && view.body != null && (
            <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: 6 }}>
                {view.lang} · {STATUS[view.status] || view.status}{view.author ? ` · ${view.author}` : ''}
              </div>
              {edit ? (
                <>
                  <input value={edit.title} onChange={e => setEdit(x => ({ ...x, title: e.target.value }))} style={{ width: '100%', padding: 8, marginBottom: 8, border: '1px solid var(--field-line)', borderRadius: 8, fontWeight: 700 }} />
                  <textarea value={edit.body} onChange={e => setEdit(x => ({ ...x, body: e.target.value }))} style={{ width: '100%', minHeight: 280, padding: 10, border: '1px solid var(--field-line)', borderRadius: 8, fontFamily: 'inherit', fontSize: '0.88rem', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Btn size="sm" loading={busy === 'save'} onClick={() => act('save', async () => { await api.translations.update(view.id, edit); setEdit(null); })}>Save</Btn>
                    <Btn size="sm" variant="outline" onClick={() => setEdit(null)}>Cancel</Btn>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>{view.title}</div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.88rem', lineHeight: 1.6, color: 'var(--body-ink)', maxHeight: 380, overflow: 'auto' }}>{view.body}</div>
                </>
              )}
              {!edit && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  {view.createdBy === me && ['draft', 'declined'].includes(view.status) && <Btn size="sm" variant="outline" onClick={() => setEdit({ title: view.title || '', body: view.body })}>Edit</Btn>}
                  {view.createdBy === me && view.status === 'draft' && <Btn size="sm" loading={busy === 'share'} onClick={() => act('share', () => api.translations.share(view.id))}>Share for the shelf</Btn>}
                  {data.canApprove && view.status === 'shared' && <>
                    <Btn size="sm" loading={busy === 'approve'} onClick={() => act('approve', () => api.translations.decide(view.id, 'approve'))}>Approve</Btn>
                    <Btn size="sm" variant="outline" loading={busy === 'decline'} onClick={() => act('decline', () => api.translations.decide(view.id, 'decline'))}>Decline</Btn>
                  </>}
                </div>
              )}
            </div>
          )}
          {!data.translations.length && <p style={{ fontSize: '0.86rem', color: 'var(--muted)', margin: 0 }}>No language version yet. Draft one — it stays yours until you share it and the custodian approves it.</p>}
        </div>
      )}
    </Modal>
  );
}
